(() => {
  const nonceKey = 'flexora-crm-sync-nonce';
  const pendingKey = 'flexora-crm-sync-pending';
  const pendingTtl = 30 * 60 * 1000;
  const approvedGatewayOrigin = 'https://flexora-sales-intake.whp6379773.workers.dev';
  const allowedPendingKeys = new Set([
    'v',
    'capturedAt',
    'clientSubmissionId',
    'draftId',
    'nonce',
    'nonceExpiresAt',
    'statusToken',
    'statusExpiresAt',
    'pageLanguage',
    'commitAttempted',
  ]);

  const parseJson = (value) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (_error) {
      return null;
    }
  };

  const storageGetRaw = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // CRM sync is additive; Shopify's native submission remains authoritative on this page.
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Storage is optional.
    }
  };

  const hasOnlyAllowedPendingKeys = (pending) => (
    pending
    && typeof pending === 'object'
    && !Array.isArray(pending)
    && Object.keys(pending).every((key) => allowedPendingKeys.has(key))
  );

  const sanitizeStoredPending = () => {
    storageRemove(nonceKey);
    const pending = parseJson(storageGetRaw(pendingKey));
    if (!pending) return null;
    if (pending.v !== 2 || !hasOnlyAllowedPendingKeys(pending)) {
      storageRemove(pendingKey);
      return null;
    }
    return pending;
  };

  sanitizeStoredPending();

  const gatewayCandidate = document.body.dataset.crmGatewayUrl || '';
  const gatewayUrl = gatewayCandidate === approvedGatewayOrigin ? approvedGatewayOrigin : '';
  const hcaptchaSitekey = document.body.dataset.crmHcaptchaSitekey || '';
  const pageHandle = document.body.dataset.pageHandle || '';
  const captchaDeadlineMs = 2 * 60 * 1000;
  const verificationDeadlineMs = 15 * 1000;
  const captchaStates = new WeakMap();
  const syncMessages = {
    EN: {
      delivered: 'Application received and synced to Flexora CRM.',
      pending: 'Application received. Processing is still in progress.',
      retry_needed: 'Application received. CRM sync needs retry.',
    },
    ES: {
      delivered: 'Solicitud recibida y sincronizada con Flexora CRM.',
      pending: 'Solicitud recibida. Tu aplicacion esta en proceso.',
      retry_needed: 'Solicitud recibida. El enlace con CRM necesita reintento.',
    },
  };

  if (!gatewayUrl || !hcaptchaSitekey) return;
  if (document.body.dataset.crmSyncInitialized === 'true') return;
  document.body.dataset.crmSyncInitialized = 'true';

  const storageGet = (key) => {
    return parseJson(storageGetRaw(key));
  };

  const safeLog = (message) => {
    console.error(`[Flexora CRM sync] ${message}`);
  };

  const statusLanguage = (pending = {}) => (
    String(pending.pageLanguage || '').toUpperCase() === 'ES' ? 'ES' : 'EN'
  );

  const publishSyncStatus = (status, pending = {}) => {
    const language = statusLanguage(pending);
    const message = syncMessages[language][status] || syncMessages.EN[status] || '';
    document.body.dataset.crmSyncStatus = status;
    let target = document.body.querySelector?.('[data-crm-sync-status]')
      || document.querySelector?.('[data-crm-sync-status]');
    if (!target && typeof document.createElement === 'function') {
      target = document.createElement('p');
      target.className = 'application-sync-status';
      target.setAttribute?.('data-crm-sync-status', '');
      target.setAttribute?.('role', 'status');
      target.setAttribute?.('aria-live', 'polite');
      document.body.appendChild?.(target);
    }
    if (!target) return;
    target.hidden = false;
    target.dataset.crmSyncStatus = status;
    target.textContent = message;
  };

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  };

  const isUuid = (value) => (
    typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );

  const isDraftId = (value) => (
    typeof value === 'string'
    && /^DRF-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );

  const isDeliveryId = (value) => typeof value === 'string' && /^DLV-[A-Za-z0-9-]+$/.test(value);

  const parseDate = (value) => (typeof value === 'string' ? Date.parse(value) : NaN);

  const isValidIso = (value) => Number.isFinite(parseDate(value));

  const isFreshIso = (value) => parseDate(value) > Date.now();

  const isStatusToken = (value) => (
    typeof value === 'string'
    && /^[A-Za-z0-9_-]{43}$/.test(value)
  );

  const validVerifyResponse = (response, body, clientSubmissionId) => (
    response.status === 201
    && body?.accepted === true
    && body.clientSubmissionId === clientSubmissionId
    && isDraftId(body.draftId)
    && isUuid(body.nonce)
    && isFreshIso(body.nonceExpiresAt)
    && isStatusToken(body.statusToken)
    && isFreshIso(body.statusExpiresAt)
  );

  const validDeliveredCommit = (response, body) => (
    response?.status === 201
    && body?.accepted === true
    && body.syncStatus === 'delivered'
    && isDeliveryId(body.deliveryId)
    && typeof body.applicationId === 'string'
    && body.applicationId.length > 0
    && typeof body.applicantId === 'string'
    && body.applicantId.length > 0
  );

  const validPendingCommit = (response, body) => (
    response?.status === 202
    && body?.accepted === true
    && ['pending', 'delayed'].includes(body.syncStatus)
    && isDeliveryId(body.deliveryId)
  );

  const verificationTimeoutError = () => new Error('CRM verification timed out');

  const createVerificationDeadline = (deadlineMs = verificationDeadlineMs) => {
    if (typeof window.AbortController !== 'function') {
      throw new Error('CRM verification is unavailable');
    }
    const controller = new window.AbortController();
    let timerId;
    const timeout = new Promise((_resolve, reject) => {
      timerId = window.setTimeout(() => {
        reject(verificationTimeoutError());
        controller.abort();
      }, deadlineMs);
    });
    return {
      clear() {
        window.clearTimeout(timerId);
      },
      signal: controller.signal,
      waitFor(operation) {
        return Promise.race([operation, timeout]);
      },
    };
  };

  const requestVerifiedNonce = async (application, signal) => {
    let response;
    try {
      response = await fetch(`${gatewayUrl}/api/intake/verify`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ application }),
        signal,
      });
    } catch (error) {
      if (signal?.aborted || error?.name === 'AbortError') throw verificationTimeoutError();
      throw error;
    }
    if (!response.ok) throw new Error('CRM verification failed');
    const issued = await readJson(response);
    if (!validVerifyResponse(response, issued, application.clientSubmissionId)) {
      throw new Error('CRM verification failed');
    }
    return issued;
  };

  const fieldValue = (form, name) => form.elements.namedItem(name)?.value?.trim() || '';
  const createSubmissionId = () => {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    });
  };
  const captureApplication = (form) => {
    if (!form.dataset.crmClientSubmissionId) form.dataset.crmClientSubmissionId = createSubmissionId();
    const submittedAt = fieldValue(form, 'contact[submitted_at]')
      || fieldValue(form, 'contact[submitted_at]')
      || new Date().toISOString();
    return {
      clientSubmissionId: form.dataset.crmClientSubmissionId,
      fullName: fieldValue(form, 'contact[name]'),
      adultConfirmed: Number(fieldValue(form, 'contact[age]')) >= 18,
      phone: fieldValue(form, 'contact[phone]'),
      email: fieldValue(form, 'contact[email]'),
      pageLanguage: fieldValue(form, 'contact[page_language]').toUpperCase(),
      sourcePage: window.location.pathname,
      submittedAt,
      consentVersion: fieldValue(form, 'contact[consent_version]'),
      consentedAt: submittedAt,
      utm: {
        source: fieldValue(form, 'contact[utm_source]'),
        medium: fieldValue(form, 'contact[utm_medium]'),
        campaign: fieldValue(form, 'contact[utm_campaign]'),
        content: fieldValue(form, 'contact[utm_content]'),
        term: fieldValue(form, 'contact[utm_term]'),
      },
    };
  };

  const capturePending = (application, proof) => {
    storageSet(pendingKey, {
      v: 2,
      capturedAt: Date.now(),
      clientSubmissionId: application.clientSubmissionId,
      draftId: proof.draftId,
      nonce: proof.nonce,
      nonceExpiresAt: proof.nonceExpiresAt,
      statusToken: proof.statusToken,
      statusExpiresAt: proof.statusExpiresAt,
      pageLanguage: application.pageLanguage,
      commitAttempted: false,
    });
  };

  const pendingApplicationFor = (form) => {
    return captureApplication(form);
  };

  const normalizePending = (pending) => ({
    v: pending.v,
    capturedAt: pending.capturedAt,
    clientSubmissionId: pending.clientSubmissionId,
    draftId: pending.draftId,
    nonce: pending.nonce,
    nonceExpiresAt: pending.nonceExpiresAt,
    statusToken: pending.statusToken,
    statusExpiresAt: pending.statusExpiresAt,
    pageLanguage: String(pending.pageLanguage || 'EN').toUpperCase(),
    commitAttempted: pending.commitAttempted === true,
  });

  const validPending = (pending) => {
    if (!pending || pending.v !== 2 || !hasOnlyAllowedPendingKeys(pending)) return false;
    return Number.isFinite(pending.capturedAt)
      && Date.now() - pending.capturedAt <= pendingTtl
      && isUuid(pending.clientSubmissionId)
      && isDraftId(pending.draftId)
      && isUuid(pending.nonce)
      && isValidIso(pending.nonceExpiresAt)
      && isStatusToken(pending.statusToken)
      && isValidIso(pending.statusExpiresAt)
      && ['EN', 'ES'].includes(String(pending.pageLanguage || 'EN').toUpperCase())
      && (pending.commitAttempted === undefined || typeof pending.commitAttempted === 'boolean');
  };

  const getPending = () => {
    const pending = storageGet(pendingKey);
    if (!validPending(pending)) {
      storageRemove(pendingKey);
      return null;
    }
    return normalizePending(pending);
  };

  const canCommit = (pending) => (
    pending.commitAttempted === false
    && isUuid(pending.nonce)
    && isFreshIso(pending.nonceExpiresAt)
  );

  const canRecoverStatus = (pending) => (
    isStatusToken(pending.statusToken)
    && isFreshIso(pending.statusExpiresAt)
  );

  const markCommitAttempted = (pending) => {
    const updated = normalizePending(pending);
    updated.commitAttempted = true;
    storageSet(pendingKey, updated);
    return updated;
  };

  storageRemove(nonceKey);
  getPending();

  const waitForHcaptcha = async () => {
    for (let attempt = 0; attempt < 100; attempt += 1) {
      if (typeof window.hcaptcha?.render === 'function') return window.hcaptcha;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    throw new Error('CRM hCaptcha is unavailable');
  };

  const executeGatewayCaptcha = async (form, signal) => {
    const hcaptcha = await waitForHcaptcha();
    let state = captchaStates.get(form);
    if (!state) {
      const target = form.querySelector?.('[data-crm-hcaptcha]');
      if (!target) throw new Error('CRM hCaptcha target is unavailable');
      state = {};
      state.widgetId = hcaptcha.render(target, {
        sitekey: hcaptchaSitekey,
        size: 'invisible',
        callback(token) {
          state.resolve?.(token);
          state.resolve = null;
          state.reject = null;
        },
        'error-callback'() {
          state.reject?.(new Error('CRM hCaptcha failed'));
          state.resolve = null;
          state.reject = null;
        },
        'expired-callback'() {
          state.reject?.(new Error('CRM hCaptcha expired'));
          state.resolve = null;
          state.reject = null;
        },
      });
      captchaStates.set(form, state);
    }
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (handler, value) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', abort);
        state.resolve = null;
        state.reject = null;
        handler(value);
      };
      const abort = () => settle(reject, verificationTimeoutError());
      state.resolve = (token) => settle(resolve, token);
      state.reject = (error) => settle(reject, error);
      if (signal?.aborted) {
        abort();
        return;
      }
      signal?.addEventListener('abort', abort, { once: true });
      try {
        hcaptcha.reset?.(state.widgetId);
        hcaptcha.execute(state.widgetId);
      } catch (_error) {
        state.reject(new Error('CRM hCaptcha failed'));
      }
    });
  };

  const resumeNativeSubmit = (form, submitter) => {
    if (typeof form.requestSubmit === 'function') {
      if (submitter) {
        const submitterWasDisabled = submitter.disabled;
        submitter.disabled = false;
        try {
          form.requestSubmit(submitter);
        } finally {
          submitter.disabled = submitterWasDisabled;
        }
      } else form.requestSubmit();
      return;
    }
    submitter?.click?.();
  };

  const deliveryStatus = async (pending) => {
    try {
      const response = await fetch(`${gatewayUrl}/api/intake/status`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          clientSubmissionId: pending.clientSubmissionId,
          draftId: pending.draftId,
          statusToken: pending.statusToken,
          recover: true,
        }),
      });
      if (!response.ok) return null;
      const body = await readJson(response);
      if (validDeliveredCommit(response, body) || validPendingCommit(response, body)) {
        return { response, body };
      }
      return null;
    } catch (_error) {
      return null;
    }
  };

  const clearPending = () => {
    storageRemove(pendingKey);
    storageRemove(nonceKey);
  };

  const recoverPendingStatus = async (pending) => {
    if (!canRecoverStatus(pending)) {
      publishSyncStatus('retry_needed', pending);
      clearPending();
      safeLog('Application sync expired; application needs retry.');
      return false;
    }
    const status = await deliveryStatus(pending);
    if (status && validDeliveredCommit(status.response, status.body)) {
      publishSyncStatus('delivered', pending);
      clearPending();
      return true;
    }
    if (status && validPendingCommit(status.response, status.body)) {
      publishSyncStatus('pending', pending);
      clearPending();
      return true;
    }
    publishSyncStatus('retry_needed', pending);
    safeLog('Application sync needs retry.');
    return false;
  };

  const commitPending = async () => {
    let pending = getPending();
    if (!pending) return;
    if (!canCommit(pending)) {
      await recoverPendingStatus(pending);
      return;
    }
    pending = markCommitAttempted(pending);
    try {
      const response = await fetch(`${gatewayUrl}/api/intake/commit`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          clientSubmissionId: pending.clientSubmissionId,
          draftId: pending.draftId,
          nonce: pending.nonce,
        }),
      });
      const body = await readJson(response);
      if (validDeliveredCommit(response, body)) {
        publishSyncStatus('delivered', pending);
        clearPending();
        return;
      }
      if (validPendingCommit(response, body)) {
        publishSyncStatus('pending', pending);
        clearPending();
        return;
      }
      throw new Error('invalid_commit_response');
    } catch (_error) {
      await recoverPendingStatus(pending);
    }
  };

  document.querySelectorAll('[data-crm-sync-payload]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      if (form.dataset.crmSyncPrepared === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation?.();
      if (form.dataset.crmSyncInFlight === 'true') return;
      form.dataset.crmSyncInFlight = 'true';
      const submitter = event.submitter;
      const submitterWasDisabled = submitter?.disabled;
      if (submitter) submitter.disabled = true;
      void (async () => {
        const syncError = form.querySelector?.('[data-crm-sync-error]');
        let captchaDeadline;
        let verificationDeadline;
        try {
          if (syncError) syncError.hidden = true;
          captchaDeadline = createVerificationDeadline(captchaDeadlineMs);
          const application = pendingApplicationFor(form);
          const captchaToken = await captchaDeadline.waitFor(
            executeGatewayCaptcha(form, captchaDeadline.signal),
          );
          captchaDeadline.clear();
          verificationDeadline = createVerificationDeadline();
          const proof = await verificationDeadline.waitFor(
            requestVerifiedNonce({ ...application, captchaToken }, verificationDeadline.signal),
          );
          capturePending(application, proof);
          form.dataset.crmSyncPrepared = 'true';
          resumeNativeSubmit(form, submitter);
        } catch (error) {
          delete form.dataset.crmSyncInFlight;
          if (submitter) submitter.disabled = submitterWasDisabled;
          storageRemove(nonceKey);
          storageRemove(pendingKey);
          if (syncError) {
            syncError.hidden = false;
            syncError.focus?.();
          }
          safeLog('Verification unavailable; application was not submitted.');
        } finally {
          captchaDeadline?.clear();
          verificationDeadline?.clear();
        }
      })();
    }, { capture: true });
  });

  if (pageHandle === 'thank-you') {
    commitPending();
  } else {
    getPending();
  }
})();
